import type { ExpandedOrganizationUser } from 'types/organization'

/**
 * Retrieves the user with a Manager role from an array of organization users
 */
export const getManagerUser = (users: ExpandedOrganizationUser[]): ExpandedOrganizationUser | undefined => {
  return users.find((user) => user.role.name === 'Manager')
}

/**
 * Gets the full name of the manager user
 */
export const getManagerName = (users: ExpandedOrganizationUser[]): string | undefined => {
  const manager = getManagerUser(users)
  return manager ? `${manager.user.firstName} ${manager.user.lastName}` : undefined
}

/**
 * Generates a shuffled array of colors for position avatars
 */
export const generatePositionColors = (positions: string[]): Record<string, string> => {
  const colors = [
    '#4e79a7',
    '#f28e2c',
    '#e15759',
    '#76b7b2',
    '#59a14f',
    '#edc949',
    '#af7aa1',
    '#ff9da7',
    '#9c755f',
    '#bab0ab',
    '#5f9ed1',
    '#c85200',
    '#898989',
    '#a2a2a2',
    '#cfcfcf',
    '#ffbc79',
  ]

  // Shuffle the colors array
  const shuffledColors = [...colors]
  for (let i = shuffledColors.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffledColors[i], shuffledColors[j]] = [shuffledColors[j], shuffledColors[i]]
  }

  return Object.fromEntries(
    positions.map((position, index) => [position, shuffledColors[index % shuffledColors.length]])
  )
}

/**
 * Calculates the width of a tree node based on team name length
 */
export const calculateNodeWidth = (teamName: string, minWidth = 200, maxWidth = 300): number => {
  return Math.min(Math.max(teamName.length * 10, minWidth), maxWidth)
}

/**
 * Counts the total number of nodes in a tree structure
 */
export const countTreeNodes = (node: { children?: any[] }): number => {
  if (!node.children) return 1
  return 1 + node.children.reduce((sum, child) => sum + countTreeNodes(child), 0)
}

/**
 * Calculates the zoom factor based on the number of nodes
 */
export const calculateZoomFactor = (nodeCount: number): number => {
  return Math.max(0.3, 1 - Math.log(nodeCount) / 10)
}
